import { Controller, useForm } from 'react-hook-form';
import {
  FeeType,
  grantAccessWithConfig,
  PlatformProductGrantAccessWithConfigRequest,
} from '../../../../../../api-lib/src/gen/ops-portal-api';
import { Autocomplete, Divider, TextField } from '@mui/material';
import React, { useState } from 'react';
import Box from '@mui/system/Box';
import { showErrors } from '@b7hio/core-lib/src/utils';
import { useSnackbar } from '@b7hio/core-lib/src';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { PlatformUidName } from './PlatformProductFormWrapper';

interface Props {
  platform: PlatformUidName;
  bankUid: string;
  productUid: string;
  defaultValues: PlatformProductFormInput;
  removePlatformFromSelected?: (uniqueId: string) => void;
}

export interface PlatformProductFormInput {
  feeType?: FeeType;
  productAlias?: string;
  bondsmithFee: number;
  partnerFee?: number;
  minDeposit?: number;
  maxDeposit?: number;
}
type KeyOfFormInput = keyof PlatformProductFormInput;

export const PlatformProductForm = ({
  platform,
  bankUid,
  productUid,
  defaultValues,
  removePlatformFromSelected,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['common', 'products']);
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<PlatformProductFormInput>({
    defaultValues,
  });
  const [loading, setIsLoading] = useState<boolean>(false);

  const onSubmit = async (
    formData: PlatformProductFormInput
  ): Promise<void> => {
    setIsLoading(true);
    try {
      await sendPlatformProductCreateRequest(platform.platformUid, formData);
      enqueueSnackbar('Created platform product', 'success');
      if (removePlatformFromSelected != null) {
        removePlatformFromSelected(platform.uniqueId);
      }
    } catch (error) {
      showErrors(error, enqueueSnackbar, 'Could not create platform product');
    }
    setIsLoading(false);
  };

  const sendPlatformProductCreateRequest = async (
    platformUid: string,
    formData: PlatformProductFormInput
  ): Promise<void> => {
    const request = generatePlatformProductCreateRequestBody(
      platformUid,
      formData
    );
    await grantAccessWithConfig(request);
  };

  const generatePlatformProductCreateRequestBody = (
    platformUid: string,
    formData: PlatformProductFormInput
  ): PlatformProductGrantAccessWithConfigRequest => {
    return {
      platformProductGrantAccessRequest: {
        bankUid,
        feeDetailRequest: {
          amount: formData.bondsmithFee,
          type: formData.feeType ?? FeeType.INTEREST,
          startDate: new Date().toISOString(),
          alias: formData.productAlias,
          platformUid,
          productUid,
        },
      },
      platformFeeStructureRequest:
        formData.partnerFee != null
          ? {
              rate: formData.partnerFee,
              reference: formData.productAlias ?? 'ref',
            }
          : null,
      platformProductConfigRequest:
        formData.minDeposit != null
          ? {
              minimumDepositRequirement: formData.minDeposit,
              maximumDepositRequirement: formData.maxDeposit,
            }
          : null,
    };
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <Box display="flex" gap="1rem">
          <Controller
            name="feeType"
            control={control}
            render={({ field }) => (
              <Autocomplete
                sx={{ flex: 1 }}
                value={field.value}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                }}
                options={Object.values(FeeType)}
                renderInput={(params) => (
                  <TextField
                    error={Boolean(errors.feeType?.type)}
                    helperText={errors.feeType?.type}
                    {...params}
                    label={t('products:platformProducts.fields.feeType')}
                  />
                )}
              />
            )}
          />
          {Object.keys(defaultValues)
            .filter((key) => key !== 'feeType')
            .map((key) => {
              return (
                <Controller
                  name={key as KeyOfFormInput}
                  key={key}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      defaultValue={value}
                      onChange={onChange}
                      sx={{ flex: 1 }}
                      label={t(
                        `products:platformProducts.fields.${key}` as any
                      )}
                      error={Boolean(errors[key as KeyOfFormInput]?.type)}
                      helperText={errors[key as KeyOfFormInput]?.type}
                    />
                  )}
                />
              );
            })}
        </Box>
        <LoadingButton
          type="submit"
          loading={loading}
          sx={{ width: 'fit-content' }}>
          Apply
        </LoadingButton>
      </form>
      <Divider />
    </>
  );
};
